import { Log } from '@/parsing/Log';
import { LogEntryType } from '@/parsing/LogEntry';
import {
  MQTTMessageLogEntryExtra,
  MQTTMessageLogEvent,
} from '@/parsing/parsers/common/MQTTMessageLogEvent';

import { TimelineMarker, TimelineRow, TimelineRowGroup } from '../../TimelineData';
import { obtainFreeRowForMarker } from '../obtainFreeRowForMarker';
import { TimelineGeneratorContext } from '../TimelineGeneratorContext';

export function generateMQTTGroupFromLog(
  log: Log,
  ctx: TimelineGeneratorContext,
): TimelineRowGroup {
  const rows = new Map<MQTTMessageLogEvent, TimelineRow[]>();
  let lastConnectionStatus: {
    mqttMessage: MQTTMessageLogEntryExtra;
    marker: TimelineMarker;
  } | null = null;

  for (const entry of log.entries) {
    if (entry.entryType !== LogEntryType.MQTTMessage) {
      continue;
    }

    const mqttMessage = entry.extra.mqttMessage!;
    const tooltipContent = getTooltipContent(mqttMessage);

    const start = entry.date;

    const marker: TimelineMarker = {
      markerId: ctx.nextMarkerId,
      logEntry: entry,
      alternativeLogEntry: undefined,
      startTimeMs: start.getTime(),
      endTimeMs: undefined,
      fillColor: getFillColor(mqttMessage.event),
      textOnMarker: getTextOnMarker(mqttMessage),
      textOnMarkerColor: getTextOnMarkerColor(mqttMessage.event),
      tooltipContent,
    };
    ctx.nextMarkerId += 1;

    if (mqttMessage.event === MQTTMessageLogEvent.ConnectionStatus) {
      marker.endTimeMs = log.entries.at(-1)?.date.getTime();
      if (lastConnectionStatus) {
        const end = entry.date;
        lastConnectionStatus.marker.endTimeMs = end.getTime();
      }
      lastConnectionStatus = { mqttMessage, marker };
    }

    if (!rows.has(mqttMessage.event) || rows.get(mqttMessage.event)?.length === 0) {
      rows.set(mqttMessage.event, []);
    }
    const row = obtainFreeRowForMarker(rows.get(mqttMessage.event)!, marker);
    row.markers.push(marker);
  }

  const rowGroup: TimelineRowGroup = {
    name: 'MQTT',
    rows: Array.from(rows.values()).flatMap((rowArray) => rowArray),
    expanded: true,
  };
  return rowGroup;
}

function getFillColor(event: MQTTMessageLogEvent): string {
  switch (event) {
    case MQTTMessageLogEvent.ConnectionStatus:
      return '#FFEB3B';
    case MQTTMessageLogEvent.ReceivedMessage:
      return '#BBDEFB';
    case MQTTMessageLogEvent.PublishedMessage:
      return '#FFE0B2';
    default:
      return 'rgb(0, 230, 42)';
  }
}

function getTextOnMarkerColor(event: MQTTMessageLogEvent): string {
  switch (event) {
    case MQTTMessageLogEvent.ConnectionStatus:
      return '#4E342E';
    case MQTTMessageLogEvent.ReceivedMessage:
      return '#0D47A1';
    case MQTTMessageLogEvent.PublishedMessage:
      return '#5D4037';
    default:
      return '#555';
  }
}

function getTextOnMarker(mqttMessage: MQTTMessageLogEntryExtra): string {
  switch (mqttMessage.event) {
    case MQTTMessageLogEvent.ConnectionStatus:
      return `Status: ${mqttMessage.params.status}` || 'Unknown status';
    case MQTTMessageLogEvent.ReceivedMessage:
    case MQTTMessageLogEvent.PublishedMessage:
      try {
        const parsedMessage = JSON.parse(mqttMessage?.params?.message ?? '{}');
        return parsedMessage?.data_type ?? 'Unknown data type';
      } catch (error) {
        return mqttMessage.command;
      }
    case MQTTMessageLogEvent.Generic:
      return mqttMessage.command;
  }
}

function getTooltipContent(mqttMessage: MQTTMessageLogEntryExtra): string {
  function convertParamsToJson(params: { [key: string]: any }): string {
    const filteredParams = Object.keys(params).reduce((acc, key) => {
      if (key !== 'message') {
        acc[key] = params[key];
      }
      return acc;
    }, {} as { [key: string]: any });

    return Object.keys(filteredParams).length > 0 ? JSON.stringify(filteredParams, null, 2) : '';
  }
  const paramsContent = convertParamsToJson(mqttMessage.params);

  return `
${mqttMessage.command}
${paramsContent ? `${paramsContent}\n\n` : '\n'}${mqttMessage.url}
    `.trim();
}
